import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtApiError } from "@/lib/api";
import { BRAND } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { ArrowRight, Mail, Lock, User, Phone, Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", company: "",
    role: sp.get("role") === "vendor" ? "vendor" : "customer",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register(form);
      nav(u.role === "vendor" ? "/vendor" : "/dashboard");
    } catch (e) {
      setErr(fmtApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-slate-950 text-white p-10 lg:p-14 relative overflow-hidden">
        <div className="hero-grain absolute inset-0" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="EquipmentHub" className="h-12 w-12 object-contain" style={{ mixBlendMode: "screen" }} />
            <span className="font-display font-black text-white">EquipmentHub India</span>
          </Link>
          <div className="mt-16 max-w-md">
            <div className="label-eyebrow text-ehi-accent mb-3">Get started free</div>
            <h2 className="font-display font-black text-4xl leading-tight">
              Join <span className="text-ehi-accent">India&apos;s largest</span><br />
              heavy equipment marketplace.
            </h2>
            <ul className="mt-6 space-y-2 text-slate-300 text-sm">
              <li>· Post requirements &amp; get quotes in hours</li>
              <li>· List your fleet with zero setup fees</li>
              <li>· Bid on used equipment auctions</li>
              <li>· KYC + verified badge for trust</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="md:hidden mb-6 flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="EquipmentHub" className="h-12 w-12 object-contain" style={{ mixBlendMode: "darken" }} />
            <span className="font-display font-black text-ehi-primary">EquipmentHub India</span>
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900">Create an account</h1>
          <p className="text-slate-600 mt-1">Already registered? <Link to="/login" className="text-ehi-primary font-semibold hover:underline">Log in</Link></p>

          <div className="mt-6 grid grid-cols-2 gap-2" data-testid="register-role-toggle">
            {[
              { v: "customer", l: "I want equipment" },
              { v: "vendor", l: "I supply equipment" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => upd("role", r.v)}
                className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${
                  form.role === r.v ? "border-ehi-primary bg-ehi-primary text-white" : "border-slate-200 text-slate-700 hover:border-slate-300 bg-white"
                }`}
                data-testid={`register-role-${r.v}`}
              >
                {r.l}
              </button>
            ))}
          </div>

          <div className="space-y-4 mt-6">
            <div>
              <label className="label-eyebrow block mb-2">Full name *</label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required value={form.name} onChange={(e) => upd("name", e.target.value)} className="pl-10" data-testid="register-name" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email *</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="pl-10" data-testid="register-email" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password *</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required minLength={6} type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} className="pl-10" data-testid="register-password" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-2">Phone</label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="pl-10" />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Company</label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.company} onChange={(e) => upd("company", e.target.value)} className="pl-10" />
                </div>
              </div>
            </div>
          </div>

          {err && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="register-error">{err}</div>}

          <button disabled={busy} className="btn-accent w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="register-submit">
            {busy ? "Creating account…" : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <p className="mt-4 text-xs text-slate-500">By continuing you accept the Terms &amp; Privacy Policy.</p>
        </form>
      </div>
    </div>
  );
}
